import React, { useState } from "react";
import { allowedViewsFor } from "../data/permissions";

interface StaffRecord {
  id: string;
  data: Record<string, string>;
  updatedAt: string;
}

const STORE_KEY = "staff_records";

interface FieldDef {
  key: string;
  label: string;
  kind: "text" | "date" | "textarea" | "select";
  options?: string[];
}

const sf = (key: string, label: string, kind: "text" | "date" | "textarea" | "select" = "text", options?: string[]): FieldDef => ({ key, label, kind, options });

const STAFF_FIELDS: FieldDef[] = [
  sf("name", "Full name"),
  sf("role", "Role (controls what they can see)", "select", ["SuperAdmin", "Manager", "Deputy Manager", "Senior Carer", "Nurse", "Carer", "Admin", "Kitchen", "Domestic", "Maintenance", "Agency"]),
  sf("email", "Email"),
  sf("phone", "Phone"),
  sf("startDate", "Start date", "date"),
  sf("status", "Status", "select", ["Active", "Probation", "Agency", "Bank", "Left"]),
  sf("dbsDate", "DBS certificate date", "date"),
  sf("dbsUpdate", "DBS update service enrolled", "select", ["Yes", "No"]),
  sf("rtw", "Right to work checked", "select", ["Yes", "No"]),
  sf("references", "Two references verified", "select", ["Yes", "No"]),
  sf("qualifications", "Qualifications and certificates"),
  sf("induction", "Induction completed", "select", ["Yes", "No"]),
  sf("supervisionDue", "Next supervision due", "date"),
  sf("appraisalDue", "Next appraisal due", "date"),
  sf("notes", "Notes", "textarea"),
];

function loadStaff(): StaffRecord[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

export default function Staff({ user }: { user?: any }) {
  const [staff, setStaff] = useState<StaffRecord[]>(loadStaff);
  const [form, setForm] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  const setField = (key: string, value: string) => setForm(current => ({ ...current, [key]: value }));

  const saveStaff = () => {
    if (!form.name) {
      setMessage("Full name is required.");
      return;
    }

    const record: StaffRecord = { id: editingId || `staff-${Date.now()}`, data: form, updatedAt: new Date().toISOString() };
    const next = [record, ...staff.filter(item => item.id !== record.id)];

    setStaff(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
    setMessage(`Staff record for ${form.name} saved.`);
    setForm({});
    setEditingId("");
  };

  const editStaff = (record: StaffRecord) => {
    setForm(record.data);
    setEditingId(record.id);
    window.scrollTo(0, 0);
  };

  const removeStaff = (id: string) => {
    const next = staff.filter(item => item.id !== id);

    setStaff(next);
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  };

  const daysTo = (value: string) => (value ? Math.ceil((new Date(value).getTime() - Date.now()) / 86400000) : null);
  const dbsDays = (value: string) => (value ? Math.ceil((new Date(value).getTime() + 3 * 365 * 86400000 - Date.now()) / 86400000) : null);

  const shown = staff.filter(item => String(item.data.name || "").toLowerCase().includes(search.toLowerCase()));

  const badge = (text: string, tone: "red" | "amber") => (
    <span className={tone === "red" ? "rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" : "rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"}>{text}</span>
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Staff Management</h2>
        <p className="text-sm text-slate-600">Full staff records with safer recruitment checks, supervision and appraisal dates, DBS renewals and role based permissions.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">{editingId ? "Edit staff record" : "Add staff member"}</h3>
        <div className="grid gap-3 md:grid-cols-2">
          {STAFF_FIELDS.map(field => {
            const value = form[field.key] || "";

            return (
              <div key={field.key} className={field.kind === "textarea" ? "md:col-span-2" : ""}>
                <label className="mb-1 block text-xs font-medium text-slate-600">{field.label}</label>
                {field.kind === "textarea" ? (
                  <textarea value={value} onChange={event => setField(field.key, event.target.value)} rows={2} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                ) : field.kind === "select" ? (
                  <select value={value} onChange={event => setField(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    <option value=""></option>
                    {(field.options || []).map(option => <option key={option}>{option}</option>)}
                  </select>
                ) : (
                  <input type={field.kind === "date" ? "date" : "text"} value={value} onChange={event => setField(field.key, event.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                )}
              </div>
            );
          })}
        </div>
        {form.role ? <div className="mt-2 text-xs text-slate-500">This role can access {allowedViewsFor({ role: form.role }).length} modules.</div> : null}
        <button onClick={saveStaff} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save staff record</button>
      </div>

      <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search staff" className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm" />

      <div className="space-y-3">
        {shown.map(record => {
          const d = record.data;
          const dbs = dbsDays(d.dbsDate || "");
          const sup = daysTo(d.supervisionDue || "");
          const app = daysTo(d.appraisalDue || "");

          return (
            <div key={record.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium text-slate-900">{d.name} <span className="text-xs text-slate-500">{d.role} | {d.status}</span></div>
                <div className="flex gap-2">
                  <button onClick={() => editStaff(record)} className="rounded-lg border border-slate-300 px-3 py-1 text-sm">Edit</button>
                  <button onClick={() => removeStaff(record.id)} className="rounded-lg border border-red-300 px-3 py-1 text-sm text-red-700">Remove</button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {dbs !== null && dbs < 0 ? badge("DBS renewal overdue", "red") : null}
                {dbs !== null && dbs >= 0 && dbs <= 90 ? badge(`DBS renewal in ${dbs} days`, "amber") : null}
                {d.rtw === "No" || d.references === "No" || d.induction === "No" ? badge("Recruitment checks incomplete", "red") : null}
                {sup !== null && sup < 0 ? badge("Supervision overdue", "red") : null}
                {sup !== null && sup >= 0 && sup <= 14 ? badge(`Supervision due in ${sup} days`, "amber") : null}
                {app !== null && app < 0 ? badge("Appraisal overdue", "red") : null}
                {app !== null && app >= 0 && app <= 30 ? badge(`Appraisal due in ${app} days`, "amber") : null}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Started {d.startDate || "-"} | Access {allowedViewsFor({ role: d.role }).length} modules | DBS {d.dbsDate || "-"} | Supervision {d.supervisionDue || "-"} | Appraisal {d.appraisalDue || "-"}
              </div>
            </div>
          );
        })}
        {shown.length === 0 ? <div className="text-sm text-slate-500">No staff records yet.</div> : null}
      </div>
    </div>
  );
}